import { useEffect, useMemo, useRef, useState } from "react";
import { Button, View } from "../base";
import { Icon } from "../base/Icon";

export type VoiceActionsProps = {
  voiceName: string;
  onEdit: () => void;
  onDelete: () => void;
  onDownloadPreview?: () => void;
  onDownloadOriginal?: () => void;
  hasPreview: boolean;
  hasOriginal: boolean;
};

type VoiceActionOption = {
  key: string;
  label: string;
  icon: string;
  color: string;
  onClick: () => void;
};

export const VoiceActions = ({ voiceName, onEdit, onDelete, onDownloadPreview, onDownloadOriginal, hasPreview, hasOriginal }: VoiceActionsProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (action?: () => void) => {
    setIsOpen(false);
    action?.();
  };

  const handleUseVoice = () => {
    const pythonCode = `yield TextToSpeechEvent(\n  text="Hello, this is a custom voice!",\n  voice="${voiceName}"\n)`;
    navigator.clipboard.writeText(pythonCode);
    setCodeCopied(true);
    setTimeout(() => {
      setCodeCopied(false);
      setIsOpen(false);
    }, 1000);
  };

  const options = useMemo(() => {
    return [
      {
        key: "copy-code",
        label: codeCopied ? "Code copied" : "Use this Voice",
        icon: codeCopied ? "check" : "code-block",
        color: codeCopied ? "green" : "gray",
        onClick: codeCopied ? () => {} : () => handleUseVoice(),
      },
      hasPreview && onDownloadPreview
        ? {
            key: "download-preview",
            label: "Download Preview",
            icon: "download",
            color: "gray",
            onClick: () => handleSelect(onDownloadPreview),
          }
        : null,
      hasOriginal && onDownloadOriginal
        ? {
            key: "download-original",
            label: "Download Original",
            icon: "download",
            color: "gray",
            onClick: () => handleSelect(onDownloadOriginal),
          }
        : null,
      {
        key: "edit",
        label: "Edit Voice",
        icon: "edit",
        color: "gray",
        onClick: () => handleSelect(onEdit),
      },
      {
        key: "delete",
        label: "Delete Voice",
        icon: "trash",
        color: "red",
        onClick: () => handleSelect(onDelete),
      },
    ].filter(Boolean) as VoiceActionOption[];
  }, [codeCopied]);

  return (
    <View
      ref={menuRef}
      className="relative"
    >
      <Button
        onClick={() => setIsOpen((value) => !value)}
        variant="muted"
        size="sm"
        className="!w-8 !h-8 !p-0 !rounded-full"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Voice actions"
      >
        <Icon
          name="more-vertical"
          size={20}
        />
      </Button>
      {isOpen && (
        <View
          role="menu"
          aria-label="Voice actions"
          className="absolute right-0 mt-2 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900/90 z-20 py-1"
        >
          {options.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={option.onClick}
              role="menuitem"
              className={`flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left text-sm transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-800 text-${option.color}-600 dark:text-${option.color}-400`}
            >
              {option.label}
              <Icon
                name={option.icon}
                size={16}
                className={`text-${option.color}-500 dark:text-${option.color}-400`}
              />
            </button>
          ))}
        </View>
      )}
    </View>
  );
};
